/**
 * Standard Texts Module - Simplified UI
 * Manages reusable text snippets organized in categories
 */

import { apiCall } from '../core/api.js';
import { auth } from '../core/auth.js';
import { showError, showSuccess } from '../core/ui.js';
import { getCurrentUser } from '../utils/helpers.js';
import { t } from '../core/i18n.js';

/**
 * State management for standard texts
 */
const standardTextsState = {
    structure: [],
    nextNodeId: 1,
    expandedCategories: new Set(),
    accountId: null,
    hasUnsavedChanges: false,
    autoSaveTimeout: null
};

/**
 * Show standard texts editor
 */
export async function showStandardTexts() {
    const container = document.getElementById('standard-texts-view');
    if (!container) {
        console.error('Standard texts view container not found');
        return;
    }
    
    const user = getCurrentUser();
    if (!user || !user.account_id) {
        showError('Please login to manage standard texts');
        return;
    }
    
    standardTextsState.accountId = user.account_id;
    
    // Load existing structure
    await loadStandardTexts();
    
    // Render the editor
    renderStandardTextsEditor();
}

/**
 * Load standard texts from server
 */
async function loadStandardTexts() {
    try {
        const response = await apiCall(`/standard-texts/structure?account_id=${standardTextsState.accountId}`, {
            headers: auth.getAuthHeaders()
        });
        
        if (response && response.structure) {
            standardTextsState.structure = response.structure;
            standardTextsState.nextNodeId = Math.max(...getAllNodeIds(response.structure), 0) + 1;
        } else {
            standardTextsState.structure = [];
            standardTextsState.nextNodeId = 1;
        }
    } catch (error) {
        console.error('Error loading standard texts:', error);
        standardTextsState.structure = [];
        standardTextsState.nextNodeId = 1;
    }
}

/**
 * Render the standard texts editor
 */
function renderStandardTextsEditor() {
    const container = document.getElementById('standard-texts-view');
    if (!container) return;
    
    container.innerHTML = `
        <div class="h-full p-6 overflow-y-auto">
            <div class="max-w-4xl mx-auto">
                <div class="flex items-center justify-between mb-6">
                    <div>
                        <h3 class="text-lg font-medium text-gray-900">${t('standardTexts.title')}</h3>
                        <p class="text-sm text-gray-500 mt-1">${t('standardTexts.description')}</p>
                    </div>
                    <div class="flex items-center space-x-3">
                        <button onclick="window.addStandardTextCategory()" 
                                class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700">
                            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                            </svg>
                            ${t('standardTexts.addCategory')}
                        </button>
                        <button id="save-standard-texts-btn" onclick="window.saveStandardTexts()" 
                                class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700">
                            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                            </svg>
                            ${t('standardTexts.saveAll')}
                        </button>
                    </div>
                </div>
                
                <div id="standard-texts-categories" class="space-y-3">
                    <!-- Categories will be rendered here -->
                </div>
            </div>
        </div>
    `;
    
    renderStandardTextsCategories();
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Update save button state based on unsaved changes
 */
function updateSaveButtonState() {
    const saveBtn = document.getElementById('save-standard-texts-btn');
    if (!saveBtn) return;
    
    if (standardTextsState.hasUnsavedChanges) {
        // Unsaved changes - show normal Save All button
        saveBtn.disabled = false;
        saveBtn.className = 'inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700';
        saveBtn.innerHTML = `
            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
            ${t('standardTexts.saveAll')}
        `;
    } else {
        // Everything saved - show Saved! state
        saveBtn.disabled = true;
        saveBtn.className = 'inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600';
        saveBtn.innerHTML = `
            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
            ${t('standardTexts.saved')}
        `;
    }
}

/**
 * Trigger auto-save after changes (debounced)
 */
function triggerAutoSave() {
    // Clear any pending auto-save
    if (standardTextsState.autoSaveTimeout) {
        clearTimeout(standardTextsState.autoSaveTimeout);
    }
    
    // Mark as unsaved immediately
    standardTextsState.hasUnsavedChanges = true;
    updateSaveButtonState();
    
    // Auto-save after 1 second of no changes
    standardTextsState.autoSaveTimeout = setTimeout(async () => {
        await window.saveStandardTexts();
    }, 1000);
}

/**
 * Set up warning when leaving page with unsaved changes
 */
function setupUnsavedChangesWarning() {
    window.addEventListener('beforeunload', (e) => {
        if (standardTextsState.hasUnsavedChanges) {
            e.preventDefault();
            e.returnValue = ''; // Required for Chrome
            return ''; // Some browsers show this message
        }
    });
}

// ============================================================================
// Rendering Functions
// ============================================================================

/**
 * Render all categories
 */
function renderStandardTextsCategories() {
    const container = document.getElementById('standard-texts-categories');
    if (!container) return;
    
    if (standardTextsState.structure.length === 0) {
        container.innerHTML = `
            <div class="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                </svg>
                <p class="mt-2 text-sm text-gray-500">${t('standardTexts.noCategories')}</p>
                <p class="text-xs text-gray-400">${t('standardTexts.clickToStart')}</p>
            </div>
        `;
        return;
    }
    
    container.innerHTML = standardTextsState.structure.map(category => renderCategory(category)).join('');
}

/**
 * Render a single category with its texts
 */
function renderCategory(category) {
    const isExpanded = standardTextsState.expandedCategories.has(category.id);
    const texts = category.children || [];
    
    return `
        <div class="bg-white border border-gray-200 rounded-lg shadow-sm">
            <!-- Category Header -->
            <div class="flex items-center p-4 cursor-pointer hover:bg-gray-50" onclick="window.toggleCategory(${category.id})">
                <svg class="w-5 h-5 mr-3 text-gray-400 transform transition-transform ${isExpanded ? 'rotate-90' : ''}" 
                     fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
                <svg class="w-5 h-5 mr-3 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                </svg>
                <input type="text" 
                       value="${escapeHtml(category.name)}" 
                       onclick="event.stopPropagation()"
                       onchange="window.updateCategoryName(${category.id}, this.value)"
                       class="flex-1 px-2 py-1 border-0 focus:border-b-2 focus:border-primary-500 focus:outline-none text-sm font-medium text-gray-900"
                       placeholder="Category name">
                <span class="text-xs text-gray-400 mr-4">${texts.length} text${texts.length !== 1 ? 's' : ''}</span>
                <button onclick="event.stopPropagation(); window.deleteCategory(${category.id})" 
                        class="p-1 text-gray-400 hover:text-red-600"
                        title="Delete category">
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                </button>
            </div>
            
            <!-- Category Content (expandable) -->
            <div class="border-t border-gray-200 p-4 space-y-2 ${isExpanded ? '' : 'hidden'}" id="category-${category.id}-content">
                ${texts.map(text => renderTextItem(category.id, text)).join('')}
                ${renderNewTextInput(category.id)}
            </div>
        </div>
    `;
}

/**
 * Render a text item
 */
function renderTextItem(categoryId, text) {
    return `
        <div class="flex items-start space-x-2 group">
            <svg class="w-4 h-4 mt-2 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <textarea 
                onchange="window.updateTextContent(${categoryId}, ${text.id}, this.value)"
                class="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 resize-none"
                rows="2"
                placeholder="Enter text...">${escapeHtml(text.name || '')}</textarea>
            <button onclick="window.deleteText(${categoryId}, ${text.id})" 
                    class="p-2 text-gray-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Delete text">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
            </button>
        </div>
    `;
}

/**
 * Render new text input field
 */
function renderNewTextInput(categoryId) {
    return `
        <div class="flex items-start space-x-2 pt-2">
            <svg class="w-4 h-4 mt-2 text-gray-300 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
            </svg>
            <textarea 
                id="new-text-${categoryId}"
                oninput="window.handleNewTextInput(${categoryId}, this)"
                onkeydown="if(event.key==='Enter' && !event.shiftKey) { event.preventDefault(); window.addNewText(${categoryId}); }"
                class="flex-1 px-3 py-2 border border-dashed border-gray-300 rounded-md text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 resize-none"
                rows="2"
                placeholder="Type new text and press Enter or click OK..."></textarea>
            <button id="add-btn-${categoryId}" 
                    onclick="window.addNewText(${categoryId})" 
                    style="display: none;"
                    class="px-3 py-2 bg-green-600 text-white rounded-md text-sm hover:bg-green-700 whitespace-nowrap"
                    title="Add text">
                OK
            </button>
        </div>
    `;
}

// ============================================================================
// Event Handlers
// ============================================================================

/**
 * Add new category
 */
window.addStandardTextCategory = function() {
    const newCategory = {
        id: standardTextsState.nextNodeId++,
        name: 'New Category',
        text_content: null,
        children: [],
        order: standardTextsState.structure.length
    };
    
    standardTextsState.structure.push(newCategory);
    standardTextsState.expandedCategories.add(newCategory.id);
    triggerAutoSave();
    renderStandardTextsCategories();
    
    // Focus on the category name input
    setTimeout(() => {
        const inputs = document.querySelectorAll(`input[value="New Category"]`);
        if (inputs.length > 0) {
            inputs[inputs.length - 1].select();
        }
    }, 100);
};

/**
 * Toggle category expansion
 */
window.toggleCategory = function(categoryId) {
    if (standardTextsState.expandedCategories.has(categoryId)) {
        standardTextsState.expandedCategories.delete(categoryId);
    } else {
        standardTextsState.expandedCategories.add(categoryId);
    }
    renderStandardTextsCategories();
};

/**
 * Update category name
 */
window.updateCategoryName = function(categoryId, newName) {
    const category = standardTextsState.structure.find(c => c.id === categoryId);
    if (category) {
        category.name = newName;
        triggerAutoSave();
    }
};

/**
 * Delete category
 */
window.deleteCategory = function(categoryId) {
    const category = standardTextsState.structure.find(c => c.id === categoryId);
    if (!category) return;
    
    const textCount = category.children ? category.children.length : 0;
    const message = textCount > 0
        ? `Delete "${category.name}" and its ${textCount} text${textCount !== 1 ? 's' : ''}?`
        : `Delete "${category.name}"?`;
    
    if (!confirm(message)) return;
    
    standardTextsState.structure = standardTextsState.structure.filter(c => c.id !== categoryId);
    standardTextsState.expandedCategories.delete(categoryId);
    triggerAutoSave();
    renderStandardTextsCategories();
};

/**
 * Handle new text input
 */
window.handleNewTextInput = function(categoryId, textarea) {
    const btn = document.getElementById(`add-btn-${categoryId}`);
    if (btn) {
        btn.style.display = textarea.value.trim() ? 'block' : 'none';
    }
};

/**
 * Add new text to category
 */
window.addNewText = function(categoryId) {
    const textarea = document.getElementById(`new-text-${categoryId}`);
    if (!textarea || !textarea.value.trim()) return;
    
    const category = standardTextsState.structure.find(c => c.id === categoryId);
    if (!category) return;
    
    if (!category.children) {
        category.children = [];
    }
    
    const newText = {
        id: standardTextsState.nextNodeId++,
        name: textarea.value.trim(),
        text_content: null,
        children: [],
        order: category.children.length
    };
    
    category.children.push(newText);
    triggerAutoSave();
    renderStandardTextsCategories();
    
    // Keep category expanded
    standardTextsState.expandedCategories.add(categoryId);
};

/**
 * Update text content
 */
window.updateTextContent = function(categoryId, textId, newContent) {
    const category = standardTextsState.structure.find(c => c.id === categoryId);
    if (!category) return;
    
    const text = category.children.find(t => t.id === textId);
    if (text) {
        text.name = newContent;
        triggerAutoSave();
    }
};

/**
 * Delete text
 */
window.deleteText = function(categoryId, textId) {
    const category = standardTextsState.structure.find(c => c.id === categoryId);
    if (!category || !category.children) return;
    
    category.children = category.children.filter(t => t.id !== textId);
    triggerAutoSave();
    renderStandardTextsCategories();
    
    // Keep category expanded
    standardTextsState.expandedCategories.add(categoryId);
};

/**
 * Save all changes
 */
window.saveStandardTexts = async function() {
    const saveBtn = document.getElementById('save-standard-texts-btn');
    if (!saveBtn || !standardTextsState.hasUnsavedChanges) return;
    
    try {
        // Show saving state
        saveBtn.disabled = true;
        saveBtn.className = 'inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-gray-400 cursor-not-allowed';
        saveBtn.innerHTML = `
            <svg class="w-4 h-4 mr-2 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Saving...
        `;
        
        await apiCall(`/standard-texts/structure/${standardTextsState.accountId}`, {
            method: 'PUT',
            headers: auth.getAuthHeaders(),
            body: JSON.stringify({
                structure: standardTextsState.structure
            })
        });
        
        // Mark as saved
        standardTextsState.hasUnsavedChanges = false;
        updateSaveButtonState();
        
        showSuccess('Standard texts saved successfully');
        
    } catch (error) {
        console.error('Error saving standard texts:', error);
        showError('Failed to save standard texts: ' + (error.message || 'Unknown error'));
        
        // Reset button on error
        updateSaveButtonState();
    }
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get all node IDs from structure
 */
function getAllNodeIds(nodes) {
    let ids = [];
    for (const node of nodes) {
        ids.push(node.id);
        if (node.children && node.children.length > 0) {
            ids = ids.concat(getAllNodeIds(node.children));
        }
    }
    return ids;
}

/**
 * Escape HTML to prevent XSS
 */
function escapeHtml(text) {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}
